import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, FolderOpen, MessageSquare, CalendarCheck,
  LogOut, Plus, Trash2, Edit, Eye, ChevronDown, ChevronUp,
  Mail, Phone, Clock, AlertCircle, FileText, MessageCircle,
  BarChart3, Wrench
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import ImageUploader from './ImageUploader';
import AnalyticsTab from './AdminAnalytics';
import BlogsTab from './AdminBlogs';
import WhatsAppTab from './AdminWhatsApp';
import {
  login, logout, isLoggedIn, getAdminStats, getProjects,
  getInquiries, getBookings, createProject, updateProject, deleteProject,
  updateInquiryStatus, updateBookingStatus, getBlogs
} from '../data/api';

// ── Login Screen ──
const LoginScreen = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await login(email, password);
      onLogin();
    } catch {
      setError('Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-8">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-blue-600 rounded-xl flex items-center justify-center mx-auto mb-4">
            <LayoutDashboard className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Admin Panel</h1>
          <p className="text-gray-500 mt-1">Yusen Design & Contract</p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && <div data-testid="login-error" className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">{error}</div>}
          <div>
            <label className="text-sm font-medium text-gray-700 mb-1 block">Email</label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="admin@yusen.com" data-testid="admin-login-email" />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700 mb-1 block">Password</label>
            <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required placeholder="Password" data-testid="admin-login-password" />
          </div>
          <Button type="submit" disabled={loading} data-testid="admin-login-submit" className="w-full bg-blue-600 hover:bg-blue-700 text-white">
            {loading ? 'Signing in...' : 'Sign In'}
          </Button>
        </form>
      </div>
    </div>
  );
};

// ── Status Badge ──
const StatusBadge = ({ status }) => {
  const styles = {
    new: 'bg-blue-100 text-blue-700',
    pending: 'bg-yellow-100 text-yellow-700',
    contacted: 'bg-purple-100 text-purple-700',
    confirmed: 'bg-green-100 text-green-700',
    completed: 'bg-green-100 text-green-700',
    closed: 'bg-gray-100 text-gray-600',
  };
  return <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${styles[status] || styles.new}`}>{status}</span>;
};

// ── Dashboard Tab ──
const DashboardTab = ({ stats }) => (
  <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
    {[
      { label: 'Total Projects', value: stats.projects, icon: FolderOpen, color: 'blue' },
      { label: 'Services', value: stats.services, icon: Wrench, color: 'green' },
      { label: 'Blog Posts', value: stats.blogs || 0, icon: FileText, color: 'cyan' },
      { label: 'Total Inquiries', value: stats.inquiries, icon: MessageSquare, color: 'purple' },
      { label: 'New Inquiries', value: stats.newInquiries, icon: AlertCircle, color: 'red' },
      { label: 'Total Bookings', value: stats.bookings, icon: CalendarCheck, color: 'indigo' },
      { label: 'Pending Bookings', value: stats.pendingBookings, icon: Clock, color: 'yellow' },
      { label: 'WhatsApp Clicks', value: stats.whatsappClicks || 0, icon: MessageCircle, color: 'green' },
      { label: 'Page Views', value: stats.pageviews || 0, icon: BarChart3, color: 'teal' },
    ].map(({ label, value, icon: Icon, color }) => (
      <div key={label} className="bg-white rounded-xl border p-5 hover:shadow-md transition-shadow">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-500">{label}</p>
            <p className="text-3xl font-bold text-gray-900 mt-1">{value}</p>
          </div>
          <div className={`w-12 h-12 rounded-xl bg-${color}-100 flex items-center justify-center`}>
            <Icon className={`w-6 h-6 text-${color}-600`} />
          </div>
        </div>
      </div>
    ))}
  </div>
);

// ── Projects Tab ──
const ProjectsTab = ({ projects, onRefresh }) => {
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ title: '', category: 'hospitality', description: '', features: '', location: '', area: '', year: '', beforeImage: '', afterImage: '', galleryImages: '' });
  const categories = { hospitality: 'F&B', retail: 'Retail', office: 'Office', residential: 'Residential', industrial: 'Industrial' };

  const handleEdit = (p) => { setEditId(p.id); setForm({ ...p, features: (p.features || []).join(', '), galleryImages: (p.galleryImages || []).join('\n') }); setShowForm(true); };

  const handleSave = async () => {
    const data = { ...form, features: form.features.split(',').map(f => f.trim()).filter(Boolean), galleryImages: form.galleryImages.split('\n').map(u => u.trim()).filter(Boolean) };
    if (editId) { await updateProject(editId, data); } else { await createProject(data); }
    setShowForm(false); setEditId(null); setForm({ title: '', category: 'hospitality', description: '', features: '', location: '', area: '', year: '', beforeImage: '', afterImage: '', galleryImages: '' }); onRefresh();
  };

  const handleDelete = async (id) => { if (window.confirm('Delete this project?')) { await deleteProject(id); onRefresh(); } };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-bold text-gray-900">{projects.length} Projects</h2>
        <Button onClick={() => { setShowForm(true); setEditId(null); }} data-testid="add-project-btn" className="bg-blue-600 text-white"><Plus className="w-4 h-4 mr-1" /> Add Project</Button>
      </div>
      {showForm && (
        <div className="bg-gray-50 border rounded-xl p-5 mb-5 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Input placeholder="Project Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="project-form-title" />
            <select className="border rounded-lg px-3 py-2 text-sm" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="project-form-category">
              {Object.entries(categories).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <Input placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          <div className="grid grid-cols-3 gap-3">
            <Input placeholder="Location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
            <Input placeholder="Area (sq ft)" value={form.area} onChange={(e) => setForm({ ...form, area: e.target.value })} />
            <Input placeholder="Year" value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} />
          </div>
          <Input placeholder="Features (comma-separated)" value={form.features} onChange={(e) => setForm({ ...form, features: e.target.value })} />
          <div className="grid grid-cols-2 gap-3">
            <ImageUploader label="Before Image" value={form.beforeImage} onChange={(url) => setForm({ ...form, beforeImage: url })} />
            <ImageUploader label="After Image" value={form.afterImage} onChange={(url) => setForm({ ...form, afterImage: url })} />
          </div>
          <ImageUploader label="Gallery Images" value={form.galleryImages} onChange={(urls) => setForm({ ...form, galleryImages: urls })} multiple />
          <div className="flex gap-2">
            <Button onClick={handleSave} data-testid="project-form-save" className="bg-green-600 text-white">Save</Button>
            <Button variant="outline" onClick={() => { setShowForm(false); setEditId(null); }}>Cancel</Button>
          </div>
        </div>
      )}
      <div className="space-y-2">
        {projects.map((p) => (
          <div key={p.id} className="bg-white border rounded-lg p-4 flex items-center gap-4 hover:shadow-sm transition-shadow">
            {p.afterImage && <img src={p.afterImage} alt="" className="w-16 h-12 object-cover rounded" />}
            <div className="flex-1 min-w-0">
              <p className="font-medium text-gray-900 truncate">{p.title}</p>
              <p className="text-xs text-gray-500">{p.location} | {p.area} | {p.year}</p>
            </div>
            <Badge variant="secondary" className="text-xs">{categories[p.category] || p.category}</Badge>
            <div className="flex gap-1">
              <button onClick={() => handleEdit(p)} className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"><Edit className="w-4 h-4" /></button>
              <button onClick={() => handleDelete(p.id)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ── Inquiries Tab ──
const InquiriesTab = ({ inquiries, onRefresh }) => {
  const [expanded, setExpanded] = useState(null);
  const handleStatus = async (idx, status) => { await updateInquiryStatus(idx, status); onRefresh(); };

  return (
    <div className="space-y-3">
      <h2 className="text-lg font-bold text-gray-900">{inquiries.length} Inquiries</h2>
      {inquiries.length === 0 && <p className="text-gray-500">No inquiries yet.</p>}
      {inquiries.map((inq, idx) => (
        <div key={`inq-${inq.createdAt || idx}`} className="bg-white border rounded-xl overflow-hidden">
          <div className="p-4 flex items-center gap-3 cursor-pointer hover:bg-gray-50" onClick={() => setExpanded(expanded === idx ? null : idx)}>
            <div className="flex-1">
              <div className="flex items-center gap-2"><p className="font-medium text-gray-900">{inq.name}</p><StatusBadge status={inq.status} /></div>
              <p className="text-xs text-gray-500 mt-0.5">{inq.email} | {inq.phone} | {inq.service}</p>
            </div>
            <span className="text-xs text-gray-400">{new Date(inq.createdAt).toLocaleDateString()}</span>
            {expanded === idx ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
          </div>
          {expanded === idx && (
            <div className="px-4 pb-4 border-t pt-3">
              <p className="text-sm text-gray-700 mb-3">{inq.message}</p>
              <div className="flex gap-2">
                {['contacted', 'completed', 'closed'].map(s => (<button key={s} onClick={() => handleStatus(idx, s)} className="px-3 py-1.5 text-xs border rounded-lg hover:bg-gray-50 capitalize">{s}</button>))}
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

// ── Bookings Tab ──
const BookingsTab = ({ bookings, onRefresh }) => {
  const [expanded, setExpanded] = useState(null);
  const handleStatus = async (idx, status) => { await updateBookingStatus(idx, status); onRefresh(); };

  return (
    <div className="space-y-3">
      <h2 className="text-lg font-bold text-gray-900">{bookings.length} Bookings</h2>
      {bookings.length === 0 && <p className="text-gray-500">No bookings yet.</p>}
      {bookings.map((b, idx) => (
        <div key={`book-${b.createdAt || idx}`} className="bg-white border rounded-xl overflow-hidden">
          <div className="p-4 flex items-center gap-3 cursor-pointer hover:bg-gray-50" onClick={() => setExpanded(expanded === idx ? null : idx)}>
            <div className="flex-1">
              <div className="flex items-center gap-2"><p className="font-medium text-gray-900">{b.name}</p><StatusBadge status={b.status} /></div>
              <p className="text-xs text-gray-500 mt-0.5">{b.service} | {b.preferredDate} {b.preferredTime}</p>
            </div>
            <span className="text-xs text-gray-400">{new Date(b.createdAt).toLocaleDateString()}</span>
            {expanded === idx ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
          </div>
          {expanded === idx && (
            <div className="px-4 pb-4 border-t pt-3 space-y-2">
              <div className="grid grid-cols-2 gap-2 text-sm">
                <p><Mail className="w-3 h-3 inline mr-1" />{b.email}</p>
                <p><Phone className="w-3 h-3 inline mr-1" />{b.phone}</p>
                <p>Project: {b.projectType}</p>
                <p>Budget: {b.budget}</p>
              </div>
              {b.message && <p className="text-sm text-gray-700">{b.message}</p>}
              <div className="flex gap-2">
                {['confirmed', 'completed', 'closed'].map(s => (<button key={s} onClick={() => handleStatus(idx, s)} className="px-3 py-1.5 text-xs border rounded-lg hover:bg-gray-50 capitalize">{s}</button>))}
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

// ── Main Admin Panel ──
const AdminPanel = () => {
  const [authenticated, setAuthenticated] = useState(isLoggedIn());
  const [tab, setTab] = useState('dashboard');
  const [stats, setStats] = useState({ projects: 0, services: 0, inquiries: 0, bookings: 0, blogs: 0, newInquiries: 0, pendingBookings: 0 });
  const [projects, setProjects] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [blogs, setBlogs] = useState([]);
  const navigate = useNavigate();

  const loadData = async () => {
    try {
      const [s, p, i, b, bl] = await Promise.all([getAdminStats(), getProjects(), getInquiries(), getBookings(), getBlogs()]);
      setStats(s); setProjects(p); setInquiries(i); setBookings(b); setBlogs(bl);
    } catch { setAuthenticated(false); }
  };

  useEffect(() => { if (authenticated) loadData(); }, [authenticated]);

  const handleLogout = () => { logout(); setAuthenticated(false); };

  if (!authenticated) return <LoginScreen onLogin={() => setAuthenticated(true)} />;

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { id: 'analytics', label: 'Analytics', icon: BarChart3 },
    { id: 'projects', label: 'Projects', icon: FolderOpen },
    { id: 'blogs', label: 'Blog Posts', icon: FileText },
    { id: 'inquiries', label: 'Inquiries', icon: MessageSquare, badge: stats.newInquiries },
    { id: 'bookings', label: 'Bookings', icon: CalendarCheck, badge: stats.pendingBookings },
    { id: 'whatsapp', label: 'WhatsApp', icon: MessageCircle },
  ];

  return (
    <div className="min-h-screen bg-gray-100" data-testid="admin-dashboard">
      <header className="bg-white border-b px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-blue-600 rounded-lg flex items-center justify-center"><LayoutDashboard className="w-5 h-5 text-white" /></div>
          <div><h1 className="text-lg font-bold text-gray-900">Yusen Admin</h1><p className="text-xs text-gray-500">Manage your website content</p></div>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={() => navigate('/')} className="text-gray-600"><Eye className="w-4 h-4 mr-1" /> View Site</Button>
          <Button variant="outline" size="sm" onClick={handleLogout} data-testid="admin-logout-btn" className="text-red-600 border-red-200 hover:bg-red-50"><LogOut className="w-4 h-4 mr-1" /> Logout</Button>
        </div>
      </header>
      <div className="flex">
        <nav className="w-56 bg-white border-r min-h-[calc(100vh-57px)] p-4">
          {tabs.map(({ id, label, icon: Icon, badge }) => (
            <button key={id} onClick={() => setTab(id)} data-testid={`admin-tab-${id}`}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg mb-1 text-sm font-medium transition-colors ${tab === id ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:bg-gray-50'}`}
            >
              <Icon className="w-4 h-4" />{label}
              {badge > 0 && <span className="ml-auto bg-red-500 text-white text-xs rounded-full px-2 py-0.5">{badge}</span>}
            </button>
          ))}
        </nav>
        <main className="flex-1 p-6">
          {tab === 'dashboard' && <DashboardTab stats={stats} />}
          {tab === 'analytics' && <AnalyticsTab />}
          {tab === 'projects' && <ProjectsTab projects={projects} onRefresh={loadData} />}
          {tab === 'blogs' && <BlogsTab blogs={blogs} onRefresh={loadData} />}
          {tab === 'inquiries' && <InquiriesTab inquiries={inquiries} onRefresh={loadData} />}
          {tab === 'bookings' && <BookingsTab bookings={bookings} onRefresh={loadData} />}
          {tab === 'whatsapp' && <WhatsAppTab />}
        </main>
      </div>
    </div>
  );
};

export default AdminPanel;
